/**
 * The building blocks of every sign-up and login screen.
 *
 * One message and one main action per screen, in plain words. Keep new auth screens
 * to these pieces.
 */

import React, { forwardRef, useEffect, useId, useRef, useState } from 'react'
import ChartiLogoIcon from '../icons/ChartiLogoIcon'
import { AuthStory, TrustPoints } from './AuthStory'
import './authFonts.css'
import styles from './AuthShell.module.css'

const TERMS_URL = 'https://charti.ai/terms'
const PRIVACY_URL = 'https://charti.ai/privacy'

export interface AuthShellProps {
  title: React.ReactNode
  subtitle?: React.ReactNode
  children?: React.ReactNode
  /** Changing this replays the settle-in when the step changes. */
  contentKey?: string
  /** The reassurances under the form. Sign-up steps only: someone returning does not need them. */
  trust?: boolean
}

export const AuthShell: React.FC<AuthShellProps> = ({ title, subtitle, children, contentKey, trust = false }) => {
  const header = useRef<HTMLElement>(null)
  const isFirstStep = useRef(true)
  const [announcement, setAnnouncement] = useState('')
  const stepKey = typeof title === 'string' ? title : contentKey

  useEffect(() => {
    const previousTitle = document.title
    return () => {
      document.title = previousTitle
    }
  }, [])

  // The browser tab names the step, and a screen reader hears what changed: focus jumps
  // straight to the next field, past any sentence explaining why this screen appeared.
  useEffect(() => {
    const heading = header.current?.querySelector('h1')?.textContent?.trim()
    if (heading) document.title = `${heading} | Charti`
    if (isFirstStep.current) {
      isFirstStep.current = false
      return
    }
    const line = header.current?.querySelector('p')?.cloneNode(true) as HTMLElement | undefined
    line?.querySelectorAll('button').forEach((button) => button.remove())
    setAnnouncement([heading, line?.textContent?.trim()].filter(Boolean).join('. '))
  }, [stepKey])

  return (
    <div className={styles.page}>
      <AuthStory />
      <main className={styles.main}>
        <p className={styles.srOnly} aria-live="polite">
          {announcement}
        </p>
        <div className={styles.column} data-step={contentKey}>
          <ChartiLogoIcon className={styles.logo} role="img" aria-label="Charti" />
          <div key={contentKey} className={styles.content}>
            <header ref={header} className={styles.header}>
              <h1 className={styles.title}>{title}</h1>
              {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
            </header>
            {children}
          </div>
          {trust && <TrustPoints className={styles.trust} />}
        </div>
      </main>
    </div>
  )
}

/** Which email this screen is about, with a way back to change it. */
export const AuthEmailLine: React.FC<{ email: string; onChange?: () => void }> = ({ email, onChange }) => (
  <>
    {email}{' '}
    <AuthLink onClick={onChange} aria-label="Change email">
      Change
    </AuthLink>
  </>
)

/**
 * The email from the previous step, hidden, so password managers fill and save the
 * password against the right account.
 */
export const AuthUsernameHint: React.FC<{ email: string }> = ({ email }) => (
  <input type="email" autoComplete="username" value={email.trim()} readOnly hidden />
)

export const AuthForm: React.FC<React.FormHTMLAttributes<HTMLFormElement>> = (props) => (
  <form className={styles.form} noValidate {...props} />
)

export interface AuthFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string
  hint?: string
  error?: string
}

export const AuthField = forwardRef<HTMLInputElement, AuthFieldProps>(
  ({ label, hint, error, className, ...input }, ref) => {
    const id = useId()
    const messageId = `${id}-message`
    return (
      <div className={styles.field}>
        <label className={styles.label} htmlFor={id}>
          {label}
        </label>
        <input
          ref={ref}
          id={id}
          className={[styles.input, error ? styles.inputInvalid : '', className].filter(Boolean).join(' ')}
          aria-invalid={error ? true : undefined}
          aria-describedby={error || hint ? messageId : undefined}
          {...input}
        />
        {error ? (
          <p key="error" id={messageId} className={styles.fieldError} role="alert">
            {error}
          </p>
        ) : (
          hint && (
            <p key="hint" id={messageId} className={styles.hint}>
              {hint}
            </p>
          )
        )}
      </div>
    )
  },
)
AuthField.displayName = 'AuthField'

export const AuthPasswordField = forwardRef<HTMLInputElement, AuthFieldProps>(
  ({ label, hint, error, ...input }, ref) => {
    const id = useId()
    const messageId = `${id}-message`
    const [visible, setVisible] = useState(false)
    return (
      <div className={styles.field}>
        <label className={styles.label} htmlFor={id}>
          {label}
        </label>
        <div className={styles.inputWrap}>
          <input
            ref={ref}
            id={id}
            type={visible ? 'text' : 'password'}
            className={[styles.input, styles.inputWithToggle, error ? styles.inputInvalid : ''].filter(Boolean).join(' ')}
            aria-invalid={error ? true : undefined}
            aria-describedby={error || hint ? messageId : undefined}
            {...input}
          />
          <button
            type="button"
            className={styles.toggle}
            onClick={() => setVisible((shown) => !shown)}
            aria-label={visible ? 'Hide password' : 'Show password'}>
            {visible ? 'Hide' : 'Show'}
          </button>
        </div>
        {error ? (
          <p key="error" id={messageId} className={styles.fieldError} role="alert">
            {error}
          </p>
        ) : (
          hint && (
            <p key="hint" id={messageId} className={styles.hint}>
              {hint}
            </p>
          )
        )}
      </div>
    )
  },
)
AuthPasswordField.displayName = 'AuthPasswordField'

export interface AuthButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary'
  /** Working on it. Not `disabled`, which would throw keyboard focus off the page. */
  busy?: boolean
}

export const AuthButton: React.FC<AuthButtonProps> = ({ variant = 'primary', type = 'button', busy = false, onClick, ...button }) => (
  <button
    type={type}
    className={variant === 'primary' ? styles.primary : styles.secondary}
    aria-disabled={busy || undefined}
    aria-busy={busy || undefined}
    onClick={busy ? (event) => event.preventDefault() : onClick}
    {...button}
  />
)

const GoogleMark = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
)

export const AuthGoogleButton: React.FC<AuthButtonProps & { lastUsed?: boolean }> = ({ busy, lastUsed, ...button }) => (
  <AuthButton variant="secondary" busy={busy} {...button}>
    <GoogleMark />
    {busy ? 'Opening Google…' : 'Continue with Google'}
    {lastUsed && !busy && <span className={styles.lastUsed}>Last used</span>}
  </AuthButton>
)

export const AuthDivider: React.FC = () => <div className={styles.divider}>or</div>

export const AuthLink: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ type = 'button', ...button }) => (
  <button type={type} className={styles.link} {...button} />
)

export const AuthNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className={styles.note}>{children}</p>
)

export const AuthTerms: React.FC<{ action: string }> = ({ action }) => (
  <p className={styles.note}>
    By {action} you agree to our{' '}
    <a className={styles.legalLink} href={TERMS_URL} target="_blank" rel="noreferrer">
      Terms
    </a>{' '}
    and{' '}
    <a className={styles.legalLink} href={PRIVACY_URL} target="_blank" rel="noreferrer">
      Privacy Policy
    </a>
    .
  </p>
)

const ALERT_TONES = {
  error: styles.alertError,
  success: styles.alertSuccess,
  info: styles.alertInfo,
}

export const AuthAlert: React.FC<{ tone?: keyof typeof ALERT_TONES; children: React.ReactNode }> = ({
  tone = 'error',
  children,
}) => (
  <div className={`${styles.alert} ${ALERT_TONES[tone]}`} role={tone === 'error' ? 'alert' : 'status'}>
    {children}
  </div>
)

export const AuthSpinner: React.FC = () => <div className={styles.spinner} aria-hidden="true" />
